import is from '@sindresorhus/is';
import { AnimatePresence } from 'framer-motion';
import i18next from 'i18next';
import type { FunctionComponent, ReactNode } from 'react';
import { memo, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useWatch } from 'react-hook-form';
import { I18nextProvider } from 'react-i18next';

import { ItemRecommendedAgents, useAutomationsTabShown } from '~/feature/agents';
import { useApplicationContext } from '~/shared/context/use-application-context';
import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent, SectionTitleIdentifier } from '~/shared/data-contract/job-profile';
import { getSkillTitle, type JobProfileSkill, type SkillProficiencyLevel } from '~/shared/data-contract/skill';
import type { LanguageCodeForJSA } from '~/shared/internationalization';
import { ConfirmationDialog } from '~/ui/core/confirmation-dialog/ConfirmationDialog';
import { TileStickyHeader } from '~/ui/core/tile/Tile';

import {
    anchorRootAttrs,
    type CommentDocumentState,
    CommentsClassificationPublisher,
    CommentsHeaderAction,
    sectionTextFromHtml,
} from '../../comments';
import type { NavigatableSidepanelItem } from '../../skills';
import { useOptionalTileAvailability, useOptionalUrlTiles } from '../../url-tiles';
import type { JobItemDetails } from '../api/use-job-item-details';
import { useSkillLimits } from '../api/use-skill-limits';
import { ContentContainer, ContentSections, FlexRowContainer, Wrapper } from '../components/Common';
import { CategoryListSkeleton, DescriptionSectionSkeleton, TitleSkeleton } from '../components/SkeletonLoaders';
import { isDetectingSkills, isTranslating } from '../hooks/use-is-generating';
import { useRequireJobProfileTemplate } from '../hooks/use-require-job-profile-template';
import { ProfileTasksSection } from '../profile-tasks/section/ProfileTasksSection';
import { ProfileTaskTitlesPreview } from '../profile-tasks/section/ProfileTaskTitlesPreview';
import { useActiveLanguage } from '../providers/ActiveLanguageProvider';
import { createWorkTaskTilePayload, JSA_WORK_TASK_TILE_TYPE } from '../work-task-tile/work-task-tile-url';
import { Actions } from './Actions';
import { JobProfileTileTabs } from './JobProfileTileTabs';
import { JobProfileTitle } from './JobProfileTitle';
import { ManualEditStoreProvider } from './ManualEditStoreProvider';
import { NotificationBar } from './notification-bar/NotificationBar';
import { OptionsMenu } from './OptionsMenu';
import { SidepanelMetadata } from './SidepanelMetadata';
import { SkillsAiSuggestions } from './SkillsAiSuggestions';
import { SkillsSection } from './SkillsSection';
import { StructuredContentSection } from './StructuredContentSection';
import { fillUnsetProficiencyLevels, useIsSkillProficiencyVisible } from './skills/skill-proficiency-visibility';
import { addSkillsToCompetencies, mergeCompetenciesFromSkills } from './skills/use-skills-with-compentencies';
import { collectStructuredTaskElements, structuredTaskSectionIds } from './structured-task-elements';
import { useJobProfileForm } from './use-job-profile-form';
import type { SaveJobProfileOptions } from './use-job-profile-form-actions';
import { useRegenerateDialogState } from './use-regenerate-dialog-state';
import { useTranslatedSectionTitle } from './use-translated-section-title';
import { VersionNoteInput } from './VersionNoteInput';

export type JobProfileCommentableConfig = {
    readonly isOpen: boolean;
    readonly onToggle: () => void;
};

const resolveMaximumSkillLimit = (
    itemType: 'job-level' | 'job-role',
    levelMaximumSkillLimit: number | undefined,
    roleMaximumSkillLimit: number | undefined,
): number | undefined => (itemType === 'job-role' ? roleMaximumSkillLimit : levelMaximumSkillLimit);

export type JobProfileContentProps = {
    readonly architectureId: string;
    readonly breadcrumbs?: ReactNode;
    readonly commentable?: JobProfileCommentableConfig;
    readonly hideActions?: boolean;
    readonly header: ReactNode;
    readonly hideMetadata?: boolean;
    readonly hideNotifications?: boolean;
    readonly isArchived?: boolean;
    readonly isEditing: boolean;
    readonly item: JobItemDetails;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly onArchive?: () => void;
    readonly onCompareLevels?: () => void;
    readonly onCompareRoles?: () => void;
    readonly onCompareVersions: (versionA: string, versionB: string, preferredLanguage?: LanguageCodeForJSA) => void;
    readonly onDeleteComplete?: () => void;
    readonly onOpenSeparatePage?: () => void;
    readonly onOpenSkillDetails: (
        skillId: string,
        proficiency: SkillProficiencyLevel | null | undefined,
        availableItems: readonly NavigatableSidepanelItem[],
        preferredLanguage?: LanguageCodeForJSA,
    ) => void;
    readonly onRestoreComplete?: () => void;
    readonly onSkillProficiencyChanged?: (skillId: string, level: SkillProficiencyLevel) => void;
    readonly onUnreview?: () => Promise<void>;
    readonly parentRoleId?: string;
    readonly permissions: {
        readonly canEdit: boolean;
    };
    readonly profile: JobProfileComponent;
    readonly selectedVersion: string | undefined;
    readonly setActiveVersion: (versionId: string) => void;
    readonly setIsEditing: (editing: boolean) => void;
};

export type ActionsAndMetadataLayoutProps = {
    readonly actionsNode: ReactNode;
    readonly hasUnsavedChanges: boolean;
    readonly isEditing: boolean;
    readonly metadataNode: ReactNode;
    readonly versionNoteInputNode: ReactNode;
};

type NotificationsSlotProps = {
    readonly architectureId: string;
    readonly hideNotifications: boolean;
    readonly itemId: string;
    readonly itemType: 'job-level' | 'job-role';
    readonly selectedVersion: string | undefined;
    readonly onDeleteComplete: (() => void) | undefined;
    readonly onRestoreComplete: (() => void) | undefined;
};

export const ActionsAndMetadataLayout: FunctionComponent<ActionsAndMetadataLayoutProps> = ({
    actionsNode,
    hasUnsavedChanges,
    isEditing,
    metadataNode,
    versionNoteInputNode,
}) => {
    const shouldShowVersionNoteInput = isEditing && hasUnsavedChanges;
    const integratedPanelClassName = isEditing
        ? shouldShowVersionNoteInput
            ? 'max-h-[10rem]'
            : 'max-h-[3.25rem]'
        : 'gap-xxs';
    const versionNoteSlotClassName = shouldShowVersionNoteInput
        ? 'grid-rows-[1fr] opacity-100 mt-xs'
        : 'grid-rows-[0fr] opacity-0 mt-0 pointer-events-none';

    return (
        <div className={`mt-12xl ${isEditing ? 'min-h-[6.25rem]' : ''}`}>
            <div
                className={`flex flex-col overflow-hidden rounded-xl bg-surface-hover p-xxs shadow-sm transition-[max-height] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${integratedPanelClassName}`}
            >
                {actionsNode}
                {isEditing ? (
                    <div
                        className={`grid overflow-hidden transition-[grid-template-rows,opacity,margin-top] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)] ${versionNoteSlotClassName}`}
                    >
                        <div className="min-h-0 overflow-hidden">
                            {shouldShowVersionNoteInput ? versionNoteInputNode : null}
                        </div>
                    </div>
                ) : (
                    metadataNode
                )}
            </div>
        </div>
    );
};

const NotificationsSlot: FunctionComponent<NotificationsSlotProps> = ({
    architectureId,
    hideNotifications,
    itemId,
    itemType,
    onDeleteComplete,
    onRestoreComplete,
    selectedVersion,
}) => {
    if (hideNotifications) {
        return null;
    }

    return (
        <div className="not-empty:mt-xxl [&:not(:empty)+*]:mt-xxl!">
            <AnimatePresence>
                <NotificationBar
                    architectureId={architectureId}
                    itemId={itemId}
                    itemType={itemType}
                    version={selectedVersion}
                    onDeleteComplete={onDeleteComplete}
                    onRestoreComplete={onRestoreComplete}
                />
            </AnimatePresence>
        </div>
    );
};

const useCommentDocumentState = ({
    activeLanguage,
    activeLanguageIndex,
    formSkills,
    isEditing,
    profile,
    resolveSectionTitle,
    watchedTranslations,
}: {
    readonly activeLanguage: LanguageCodeForJSA;
    readonly activeLanguageIndex: number;
    readonly formSkills: readonly JobProfileSkill[];
    readonly isEditing: boolean;
    readonly profile: JobProfileComponent;
    readonly resolveSectionTitle: (section: SectionTitleIdentifier) => string;
    readonly watchedTranslations: JobProfileComponent['translations'] | undefined;
}): CommentDocumentState =>
    useMemo<CommentDocumentState>(() => {
        const structuredItems =
            (isEditing
                ? (watchedTranslations?.[activeLanguageIndex]?.structured ??
                  profile.translations[activeLanguageIndex]?.structured)
                : profile.translations[activeLanguageIndex]?.structured) ?? [];
        // Task elements synthesized into TASKS-type sections share the section's id and are not
        // commentable text sections; task comments run through their own pipeline.
        const taskSectionIds = structuredTaskSectionIds(profile.sectionTags);

        return {
            sections: structuredItems
                .filter(({ id }) => id === undefined || !taskSectionIds.has(id))
                .map((structuredItem) => ({
                    id: structuredItem.id ?? structuredItem.tag,
                    title: resolveSectionTitle(structuredItem),
                    text: sectionTextFromHtml(structuredItem.value),
                })),
            skills: formSkills.map((skill) => ({
                id: skill.id,
                title: getSkillTitle(skill, activeLanguage),
            })),
        };
    }, [
        activeLanguage,
        activeLanguageIndex,
        formSkills,
        isEditing,
        profile.sectionTags,
        profile.translations,
        resolveSectionTitle,
        watchedTranslations,
    ]);

export const JobProfileContentSkeleton: FunctionComponent<{
    readonly breadcrumbs?: ReactNode;
    readonly header: ReactNode;
}> = ({ breadcrumbs, header }) => {
    return (
        <Wrapper style={{ width: '100%' }}>
            <TileStickyHeader>{header}</TileStickyHeader>
            {breadcrumbs === undefined ? null : <div className="mt-3xl">{breadcrumbs}</div>}
            <TitleSkeleton />
            <ContentContainer>
                <FlexRowContainer>
                    <ContentSections>
                        <div>
                            <DescriptionSectionSkeleton />
                            <DescriptionSectionSkeleton />
                        </div>
                        <CategoryListSkeleton />
                    </ContentSections>
                </FlexRowContainer>
            </ContentContainer>
        </Wrapper>
    );
};

const JobProfileContentInner: FunctionComponent<JobProfileContentProps> = ({
    architectureId,
    breadcrumbs,
    commentable,
    hideActions = false,
    header,
    hideMetadata = false,
    hideNotifications = false,
    isArchived,
    isEditing,
    item,
    itemId,
    itemType,
    onArchive,
    onCompareLevels,
    onCompareRoles,
    onCompareVersions,
    onDeleteComplete,
    onOpenSeparatePage,
    onOpenSkillDetails,
    onRestoreComplete,
    onSkillProficiencyChanged,
    onUnreview,
    parentRoleId,
    permissions,
    profile,
    selectedVersion,
    setActiveVersion,
    setIsEditing,
}) => {
    const isCommentable = !is.undefined(commentable);
    const showAutomationsTab = useAutomationsTabShown(isEditing);
    const translate = useTranslate();
    const { activeLanguage, activeLanguageIndex } = useActiveLanguage();
    const urlTiles = useOptionalUrlTiles();
    const tileAvailability = useOptionalTileAvailability();
    const openRecommendedTask =
        urlTiles !== null &&
        (tileAvailability === null || tileAvailability.availabilityByType.has(JSA_WORK_TASK_TILE_TYPE))
            ? ({ id, version }: { readonly id: string; readonly version: number }) =>
                  urlTiles.openTile(createWorkTaskTilePayload({ language: activeLanguage, taskId: id, version }))
            : undefined;

    const regenerateDialog = useRegenerateDialogState();
    const resolveCommentSectionTitle = useTranslatedSectionTitle(profile.sectionTags);
    const { dialog: jobProfileTemplateDialog, requireJobProfileTemplate } =
        useRequireJobProfileTemplate(architectureId);
    const showSkillProficiency = useIsSkillProficiencyVisible(itemType);

    const { openAfterMenuCloses } = regenerateDialog;
    const requestRegenerate = useCallback((): void => {
        requireJobProfileTemplate(openAfterMenuCloses);
    }, [openAfterMenuCloses, requireJobProfileTemplate]);

    const {
        allCompetencies,
        competenciesWithSkills,
        formMethods,
        formSkills,
        handleCancel,
        handleCancelDiscard,
        handleConfirmDiscard,
        handleManualEdit,
        handleMove,
        handleNewVersion,
        handleRemoveCompetency,
        handleSaveSubmit,
        handleSaveValidationError,
        hasFormChanges,
        isDiscardDialogOpen,
        onChangeProficiencyLevel,
        removeSkill,
        versions,
    } = useJobProfileForm({
        architectureId,
        isEditing,
        item,
        itemId,
        itemType,
        onSkillProficiencyChanged,
        parentRoleId,
        profile,
        selectedVersion,
        setActiveVersion,
        setIsEditing,
    });
    const watchedTranslations = useWatch({ control: formMethods.control, name: 'translations' });
    const parsedParentVersion = Number.parseInt(selectedVersion ?? '1', 10);
    const parentVersion = Number.isNaN(parsedParentVersion) || parsedParentVersion < 1 ? 1 : parsedParentVersion;
    const { data: skillLimits } = useSkillLimits({
        architectureId,
        enabled: isEditing,
    });

    const maximumSkillLimit = resolveMaximumSkillLimit(
        itemType,
        skillLimits?.levelMaximumSkillLimit,
        skillLimits?.roleMaximumSkillLimit,
    );

    const isSkillLimitReached = maximumSkillLimit !== undefined && formSkills.length >= maximumSkillLimit;

    const isGeneratingTranslations = isTranslating(item?.profileGenerationInfo);
    const isGeneratingSkills = isDetectingSkills(item?.profileGenerationInfo);

    const commentDocumentState = useCommentDocumentState({
        activeLanguage,
        activeLanguageIndex,
        formSkills,
        isEditing,
        profile,
        resolveSectionTitle: resolveCommentSectionTitle,
        watchedTranslations,
    });

    const allSkillItems: NavigatableSidepanelItem[] = useMemo(
        () => competenciesWithSkills.flatMap((competency) => competency.skills.map((skill) => ({ id: skill.id }))),
        [competenciesWithSkills],
    );

    const handleOpenSkillDetails = useCallback(
        (skillId: string, proficiency: SkillProficiencyLevel | null | undefined) => {
            onOpenSkillDetails(skillId, showSkillProficiency ? proficiency : undefined, allSkillItems, activeLanguage);
        },
        [activeLanguage, allSkillItems, onOpenSkillDetails, showSkillProficiency],
    );

    const handleSave = useCallback(
        (options: SaveJobProfileOptions): Promise<void> => {
            if (!showSkillProficiency) {
                formMethods.setValue('competencies', fillUnsetProficiencyLevels(formMethods.getValues('competencies')));
            }

            return formMethods.handleSubmit((data) => handleSaveSubmit(data, options), handleSaveValidationError)();
        },
        [formMethods, handleSaveSubmit, handleSaveValidationError, showSkillProficiency],
    );

    const headerOptionsSlot = document.getElementById('header-options-slot');

    const inlineTaskElements = useMemo(
        () => collectStructuredTaskElements(profile.sectionTags, profile.translations[activeLanguageIndex]?.structured),
        [activeLanguageIndex, profile.sectionTags, profile.translations],
    );
    const inlineTaskTitles = useMemo(
        () =>
            inlineTaskElements
                .map(({ value }) => value.trim())
                .filter((title) => title !== '')
                .map((title, order) => ({ order, title })),
        [inlineTaskElements],
    );
    // Persisted profiles carry a taskId on every synthesized task element; the template preview
    // does not (its tasks are not persisted), so it renders the generated titles with the preview
    // tasks matched by order.
    const hasJoinableInlineTasks = inlineTaskElements.some(({ taskId }) => is.nonEmptyString(taskId));
    const tasksSectionContent =
        !hasJoinableInlineTasks && inlineTaskTitles.length > 0 ? (
            <ProfileTaskTitlesPreview language={activeLanguage} tasks={profile.tasks} titles={inlineTaskTitles} />
        ) : (
            <ProfileTasksSection
                activeLanguage={activeLanguage}
                context={{
                    architectureId,
                    parentId: itemId,
                    parentType: itemType === 'job-role' ? 'role' : 'level',
                    parentVersion,
                    roleId: itemType === 'job-level' ? parentRoleId : undefined,
                }}
                inlineTasks={inlineTaskElements}
                isEditing={isEditing}
                parentCanEdit={permissions.canEdit}
                value={profile.tasks}
            />
        );

    const i18n = useMemo(() => {
        const tmp = i18next.cloneInstance();
        tmp.services.languageDetector = null;
        return tmp.cloneInstance({ lng: activeLanguage, react: { useSuspense: false } });
    }, [activeLanguage]);

    const actionsNode = !hideActions ? (
        <Actions
            isArchived={isArchived}
            isEditing={isEditing}
            isRegenerateDialogOpen={regenerateDialog.isOpen}
            item={item}
            permissions={permissions}
            selectedVersion={selectedVersion}
            versions={versions ?? []}
            onCancel={handleCancel}
            onNewVersion={handleNewVersion}
            onRegenerateDialogOpenChange={regenerateDialog.setIsOpen}
            onRequestRegenerate={requestRegenerate}
            onSave={handleSave}
            onVersionChange={(versionId) => {
                setActiveVersion(versionId);
            }}
        />
    ) : null;

    const metadataNode = !hideMetadata ? (
        <SidepanelMetadata
            architectureId={architectureId}
            isArchived={isArchived}
            itemId={itemId}
            itemType={itemType}
            version={selectedVersion}
        />
    ) : null;

    const versionNoteInputNode =
        isEditing && !hideActions ? (
            <VersionNoteInput
                disabled={isGeneratingTranslations || isGeneratingSkills}
                register={formMethods.register('versionNote')}
                onInput={handleManualEdit}
            />
        ) : null;

    const actionsAndMetadata = (
        <ActionsAndMetadataLayout
            actionsNode={actionsNode}
            hasUnsavedChanges={hasFormChanges}
            isEditing={isEditing}
            metadataNode={metadataNode}
            versionNoteInputNode={versionNoteInputNode}
        />
    );

    const profileSections = (
        <FlexRowContainer>
            <ContentSections>
                <I18nextProvider i18n={i18n}>
                    <StructuredContentSection
                        activeLanguageIndex={activeLanguageIndex}
                        afterTasksContent={tasksSectionContent}
                        commentable={isCommentable}
                        control={formMethods.control}
                        isEditing={isEditing}
                        isGeneratingTranslations={isGeneratingTranslations}
                        itemId={itemId}
                        newThreadCreationDisabled={isEditing}
                        profile={profile}
                        onUserInput={handleManualEdit}
                    />
                    <SkillsSection
                        aiSuggestions={
                            <SkillsAiSuggestions
                                architectureId={architectureId}
                                currentSkills={formSkills}
                                profile={profile}
                                showProficiency={showSkillProficiency}
                                onAcceptSkillSuggestions={(skills: JobProfileSkill[]): void => {
                                    formMethods.setValue('skills', skills);

                                    const currentCompetencies = formMethods.getValues('competencies');
                                    const baseCompetencies = mergeCompetenciesFromSkills(
                                        currentCompetencies,
                                        skills,
                                        allCompetencies,
                                    );

                                    formMethods.setValue(
                                        'competencies',
                                        addSkillsToCompetencies(baseCompetencies, skills),
                                    );
                                    handleManualEdit();
                                }}
                            />
                        }
                        architectureId={architectureId}
                        commentable={isCommentable}
                        competenciesWithSkills={competenciesWithSkills}
                        currentSkillCount={formSkills.length}
                        formErrors={formMethods.formState.errors}
                        formMethods={formMethods}
                        isEditing={isEditing}
                        isGeneratingSkills={isGeneratingSkills}
                        isSkillLimitReached={isSkillLimitReached}
                        itemId={itemId}
                        itemType={itemType}
                        maximumSkillLimit={maximumSkillLimit}
                        newThreadCreationDisabled={false}
                        showProficiency={showSkillProficiency}
                        onChangeProficiencyLevel={onChangeProficiencyLevel}
                        onManualEdit={handleManualEdit}
                        onMove={handleMove}
                        onOpenSkillDetails={handleOpenSkillDetails}
                        onRemoveCompetency={handleRemoveCompetency}
                        onRemoveSkill={removeSkill}
                    />
                </I18nextProvider>
            </ContentSections>
        </FlexRowContainer>
    );

    const content = (
        <>
            {isCommentable ? <CommentsClassificationPublisher documentState={commentDocumentState} /> : null}
            <TileStickyHeader compact={true}>{header}</TileStickyHeader>
            <div className="flex min-w-0 flex-col">
                {breadcrumbs === undefined ? null : <div className="mt-3xl">{breadcrumbs}</div>}
                <NotificationsSlot
                    architectureId={architectureId}
                    hideNotifications={hideNotifications}
                    itemId={itemId}
                    itemType={itemType}
                    selectedVersion={selectedVersion}
                    onDeleteComplete={onDeleteComplete}
                    onRestoreComplete={onRestoreComplete}
                />
                <JobProfileTitle
                    activeLanguageIndex={activeLanguageIndex}
                    architectureId={architectureId}
                    control={formMethods.control}
                    isCommentable={isCommentable}
                    isEditing={isEditing}
                    isGeneratingTranslations={isGeneratingTranslations}
                    itemId={itemId}
                    itemType={itemType}
                    register={formMethods.register}
                    selectedVersion={selectedVersion}
                    onInput={handleManualEdit}
                />
            </div>
            <ContentContainer>
                {jobProfileTemplateDialog}
                <ConfirmationDialog
                    cancelButtonLabel={translate('common.cancel')}
                    confirmButtonLabel={translate('common.confirm')}
                    confirmButtonVariant="default"
                    description={translate('dialogs.discard.content')}
                    open={isDiscardDialogOpen}
                    title={translate('dialogs.discard.title')}
                    onCancel={handleCancelDiscard}
                    onConfirm={handleConfirmDiscard}
                />
                {headerOptionsSlot !== null &&
                    isArchived !== true &&
                    ReactDOM.createPortal(
                        <div className="flex items-center gap-m">
                            {!is.undefined(commentable) ? (
                                <CommentsHeaderAction isOpen={commentable.isOpen} onToggle={commentable.onToggle} />
                            ) : null}
                            <OptionsMenu
                                isEditing={isEditing}
                                item={item}
                                selectedVersion={selectedVersion}
                                onArchive={onArchive}
                                onCompareLevels={onCompareLevels}
                                onCompareRoles={onCompareRoles}
                                onCompareVersions={onCompareVersions}
                                onNewVersion={handleNewVersion}
                                onOpenSeparatePage={onOpenSeparatePage}
                                onRegenerate={requestRegenerate}
                                onUnreview={onUnreview}
                                onVersionChange={(versionId) => {
                                    setActiveVersion(versionId);
                                }}
                            />
                        </div>,
                        headerOptionsSlot,
                    )}
                {actionsAndMetadata}
                <JobProfileTileTabs
                    architectureId={architectureId}
                    automations={
                        <ItemRecommendedAgents
                            architectureId={architectureId}
                            itemId={itemId}
                            itemType={itemType}
                            onOpenTask={openRecommendedTask}
                        />
                    }
                    isEditing={isEditing}
                    itemId={itemId}
                    itemType={itemType}
                    overview={profileSections}
                    showAutomations={showAutomationsTab}
                    tasksFallback={tasksSectionContent}
                />
            </ContentContainer>
        </>
    );

    return (
        <div className="flex w-full min-w-0 flex-col gap-0" {...anchorRootAttrs(itemId)}>
            {content}
        </div>
    );
};

const JobProfileContentImpl: FunctionComponent<JobProfileContentProps> = (props) => {
    const { applicationState } = useApplicationContext();
    const isAiChatEnabled = applicationState.featureFlagService.isFeatureFlagEnabled('ai-chat');
    const hideNotifications = props.hideNotifications === true || isAiChatEnabled;
    return (
        <ManualEditStoreProvider>
            <JobProfileContentInner {...props} hideNotifications={hideNotifications} />
        </ManualEditStoreProvider>
    );
};

export const JobProfileContent = memo(JobProfileContentImpl);
